import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Building2, Clock, ShieldCheck, Layers, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

const REASONS = [
  {
    number: "01",
    icon: Building2,
    title: "Single Point of Accountability for Office Furniture Projects",
    intro: "No multiple vendors. No coordination gaps.",
    body: "From design to manufacturing to installation — we take complete ownership of your modular office furniture project across India.",
    tagline: "You deal with one partner, one timeline, one responsibility.",
  },
  {
    number: "02",
    icon: Clock,
    title: "Predictable Timelines in Fast-Moving Business Environments",
    intro: "Delays are costly in today's competitive landscape.",
    body: "Traditional on-site carpentry leads to project delays, site mess, and workflow disruption.",
    benefits: ["Faster installation", "Clean, organized execution", "Minimal on-site disruption"],
    tagline: "So your teams can continue working without interruption.",
  },
  {
    number: "03",
    icon: ShieldCheck,
    title: "Standardized Quality from a Reliable Office Furniture Manufacturer",
    intro: "Inconsistent quality is a major risk with local, unstructured vendors.",
    benefits: ["Controlled manufacturing processes", "Uniform finishes across workstations, cabins, and partitions", "Durable, long-lasting materials"],
    tagline: "Consistent quality — across every office setup, in every city.",
  },
  {
    number: "04",
    icon: Layers,
    title: "Scalable Modular Office Furniture for Growing Businesses",
    intro: "Indian businesses grow fast — and your office should keep up.",
    body: "Whether you're setting up a new office, expanding your team, or reconfiguring your workspace — our modular office furniture and office expansion solutions adapt easily without rework, delays, or wastage.",
  },
  {
    number: "05",
    icon: Users,
    title: "Reduced Coordination Burden for Procurement & Admin Teams",
    intro: "Managing multiple vendors across cities and timelines is a challenge in itself.",
    benefits: ["Acting as a single execution partner", "Providing clear timelines and communication", "Managing complete site coordination"],
    tagline: "Giving your procurement and admin teams complete peace of mind.",
  },
  {
    number: "06",
    icon: Sparkles,
    title: "Professional Office Execution That Matches Corporate Standards",
    intro: "Your office space reflects your brand — especially in a competitive market.",
    benefits: ["Clean, modern, and functional workspace designs", "Professional installation processes", "A workplace aligned with your corporate image"],
  },
];

const OUTCOMES = [
  "Faster office setup across India",
  "Controlled costs and predictable timelines",
  "Zero coordination stress",
  "Ready-to-use, professionally executed workspaces",
];

export default function WhyChooseEzeefit() {
  useCanonical("https://ezeefitofficesystems.in/why-choose-ezeefit");
  useMetaDescription("Why Ezeefit for modular office furniture — single-point accountability, predictable timelines, standardized quality & turnkey corporate setups across India.");
  useEffect(() => {
    document.title = "Why Choose Ezeefit | Modular Office Furniture Pan India | Corporate Office Furniture Solutions";
  }, []);

  return (
    <div className="pt-20" data-testid="why-choose-page">
      <Helmet>
        <meta name="description" content="Why Ezeefit for modular office furniture — single-point accountability, predictable timelines, standardized quality & turnkey corporate setups across India." />
        <meta name="keywords" content="modular office furniture India, office furniture manufacturer, office workstation, office setup India, office furniture pan India, corporate office furniture" />
        <link rel="canonical" href="https://ezeefitofficesystems.in/why-choose-ezeefit" />
      </Helmet>

      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#0F172A]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/about" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors text-sm">
              <ArrowLeft className="w-4 h-4" /> About Us
            </Link>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Ezeefit Office Systems
            </h1>
            <p className="text-lg text-[#F97316] font-medium max-w-2xl mx-auto">
              Because Office Setup Shouldn't Slow Down Your Business
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro Copy */}
      <section className="py-14 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-lg text-[#334155] leading-relaxed mb-5">
              In today's fast-paced business environment, setting up or expanding an office is often unpredictable, time-consuming, and operationally disruptive.
            </p>
            <p className="text-[#64748B] leading-relaxed mb-5">
              Multiple vendors, on-site carpentry chaos, tight timelines, and constant follow-ups can delay your operations and impact productivity. Whether you need office furniture for a new setup or are looking for commercial office furniture for an expanding team, the process can be overwhelming.
            </p>
            <p className="text-[#0F172A] font-semibold text-lg mb-5">
              At Ezeefit Office Systems, we eliminate that complexity.
            </p>
            <p className="text-[#64748B] leading-relaxed">
              We provide end-to-end <strong className="text-[#334155]">modular office furniture solutions across India</strong> — ensuring your workspace is delivered on time, on budget, and without disrupting your business operations. From office furniture setup services to complete office expansion solutions, we handle it all.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section Title */}
      <section className="pt-16 pb-4 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-3 block">
              What Makes Us Different
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">
              What Makes Ezeefit the Right Choice for Corporates Across India
            </h2>
          </motion.div>
        </div>
      </section>

      {/* 6 Reasons */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 space-y-12">
          {REASONS.map((reason, index) => {
            const Icon = reason.icon;
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={reason.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`grid md:grid-cols-[56px_1fr] gap-5 ${index < REASONS.length - 1 ? "pb-12 border-b border-[#E2E8F0]" : ""}`}
              >
                {/* Number + Icon */}
                <div className="flex md:flex-col items-center md:items-start gap-3">
                  <div className="w-14 h-14 bg-[#0F172A] flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-[#F97316]" />
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-[#0F172A] mb-3">
                    {reason.title}
                  </h3>
                  <p className="text-[#F97316] font-medium text-sm mb-3">{reason.intro}</p>

                  {reason.body && (
                    <p className="text-[#64748B] leading-relaxed mb-3">{reason.body}</p>
                  )}

                  {reason.benefits && (
                    <ul className="space-y-2 mb-3">
                      {reason.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-[#334155]">
                          <span className="w-1.5 h-1.5 bg-[#F97316] rounded-full mt-2.5 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}

                  {reason.tagline && (
                    <p className="text-[#334155] font-medium">{reason.tagline}</p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-10">
              The Outcome You Can Expect
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {OUTCOMES.map((outcome, i) => (
                <motion.div
                  key={outcome}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-white/5 border border-white/10 p-5"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
                  <span className="text-white font-medium">{outcome}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">
              Work With a Trusted Modular Office Furniture Partner
            </h2>
            <p className="text-[#64748B] leading-relaxed mb-8">
              At Ezeefit Office Systems, we don't just supply furniture — we deliver structured, scalable, and reliable <strong className="text-[#334155]">office furniture solutions across India</strong>.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-5 font-semibold uppercase tracking-wider text-sm">
                <Link to="/contact">
                  Request a Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white rounded-none px-8 py-5 font-semibold uppercase tracking-wider text-sm">
                <Link to="/case-studies">
                  View Our Projects
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
            <p className="text-xs text-[#94A3B8] mt-6">
              Serving corporate offices across India — Mumbai, Navi Mumbai, Thane, Pune, Bangalore, Delhi NCR, Hyderabad, Chennai, and more. Reliable office furniture setup and installation services pan India.
            </p>
          </motion.div>
        </div>
      </section>

      <ExploreMore currentPage="manufacturing" />
    </div>
  );
}
